export interface LinkInfo {
    isExternal: boolean;
    isOpenAccessUrl: boolean;
    relativeURL: string;
}

/**
 * Get basic info about the link, including whether it's internal or external,
 * and the relative URL (regardless of whether it's internal or external).
 *
 * Relative URLs are always internal, e.g. /foo/var.
 *
 * Absolute URLs can be internal or external, depending on whether the host matches the
 * current browser host.
 */
export function getLinkInfo(link: string, externalRelativeToOrigin = window.location.origin): LinkInfo {
    return link
        ? {
              isExternal: isExternalURL(link.trim(), externalRelativeToOrigin),
              isOpenAccessUrl: getRelativeURL(link.trim()).startsWith('/openaccess'),
              relativeURL: link ? getRelativeURL(link.trim()) : ''
          }
        : {
              isExternal: false,
              isOpenAccessUrl: false,
              relativeURL: ''
          };
}

function isExternalURL(url: string, externalRelativeToOrigin = window.location.origin) {
    try {
        return isAbsoluteURL(url) && new URL(url).origin !== externalRelativeToOrigin;
    } catch {
        return false;
    }
}

function isAbsoluteURL(url: string) {
    return url?.indexOf('://') > 0 || url?.startsWith('mailto:'); // markdown-generated links can be mailto links
}

function getRelativeURL(link: string) {
    try {
        if (isAbsoluteURL(link)) {
            const url = new URL(link);
            return `${url.pathname}${url.search}${url.hash}`;
        }
        return link;
    } catch {
        return link;
    }
}

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

// Whether a string is safe to use as a navigable link (href, window.open target) for
// untrusted/data-driven values. Allowlists http(s)/mailto rather than blocklisting known-dangerous
// schemes like javascript:/data:.
export function isSafeUrl(link: string | undefined | null): boolean {
    const trimmed = link?.trim();
    if (!trimmed) {
        return false;
    }
    try {
        return SAFE_URL_PROTOCOLS.has(new URL(trimmed, window.location.origin).protocol);
    } catch {
        return false;
    }
}

/**
 * Follows a link from data the app doesn't control: only safe schemes are followed, other sites and
 * open-access pages open in a new tab, and links into the app navigate in place.
 */
export function openLink(link: string, navigate: (path: string) => void): void {
    if (!isSafeUrl(link)) {
        return;
    }

    const linkInfo = getLinkInfo(link);
    if (linkInfo.isExternal || linkInfo.isOpenAccessUrl) {
        window.open(link, '_blank', 'noopener,noreferrer');
    } else {
        navigate(linkInfo.relativeURL);
    }
}
